/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.com/docs/browser-apis/
 */
import React from "react"
import type { GatsbyBrowser } from "gatsby"
import {
  ThemeProvider,
  Theme,
  StyledEngineProvider,
} from "@mui/material/styles"
import theme from "./src/material-ui/theme"
import { AuthContextProvider } from "./src/contexts/authContext"
import { CheckoutProvider } from "./src/checkout/state"
import { BannerProvider } from "./src/components/ui/BannerProvider"
import { SiteConfigInitializer } from "./src/components/SiteConfigInitializer"

// Gatsby scrolls to a #hash as soon as the new page renders, but swaps in the
// new page's <head> about a frame later. Anything in the head that changes
// layout - per-page prototype CSS above all (see src/utils/prototypeStyles.ts)
// - then moves the target after it has been scrolled to. Measured from / to
// /about#values: the home page's `.hero { min-height: 790px }` was still
// applied when Gatsby scrolled, so the section landed 264px off once it went.
// The home page's font-size overrides add to it gradually, because
// MarkdownContent carries `transition: 1s`, so the text eases back over a
// second rather than snapping.
//
// Gatsby's own scroll still happens. This re-aligns whenever the page's layout
// changes while it settles, and stops as soon as the visitor scrolls, taps or
// types, so it never pulls back someone who has already moved. The window
// outlasts that 1s transition.
const HASH_SETTLE_MS = 1500
const USER_SCROLL_EVENTS = ["wheel", "touchstart", "keydown", "mousedown"]

const keepHashTargetInView = (id: string) => {
  const observer = new ResizeObserver(() =>
    document.getElementById(id)?.scrollIntoView()
  )
  const stop = () => {
    observer.disconnect()
    clearTimeout(timer)
    USER_SCROLL_EVENTS.forEach(e => window.removeEventListener(e, stop))
  }
  observer.observe(document.body)
  const timer = setTimeout(stop, HASH_SETTLE_MS)
  USER_SCROLL_EVENTS.forEach(e =>
    window.addEventListener(e, stop, { passive: true })
  )
  return stop
}

let stopKeepingHashTarget = () => {}

export const shouldUpdateScroll: GatsbyBrowser["shouldUpdateScroll"] = ({
  routerProps: { location },
}) => {
  // Any navigation supersedes the last one's re-alignment.
  stopKeepingHashTarget()
  stopKeepingHashTarget = location.hash
    ? // Decoded the same way Gatsby's scroll handler looks the target up.
      keepHashTargetInView(decodeURI(location.hash).substring(1))
    : () => {}
  return true
}

export const wrapRootElement = ({ element }) => {
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CheckoutProvider>
          <AuthContextProvider>
            <SiteConfigInitializer />
            <BannerProvider>{element}</BannerProvider>
          </AuthContextProvider>
        </CheckoutProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  )
}
